// The book as the editor changes it.
//
// Every operation the Lua editor performs on a book lives here, in the same
// terms: pages are scenes, a scene has a background per layout with two
// keyframes, a subtitle per layout, sounds, and tap areas
// (book_player/player_state.lua, side_panels/*.lua).
//
// The document holds content keys; a value belongs to a language, so writing
// an image or a text goes through `setText` on the key rather than on the
// page. That is why these take the editor and not a plain object.
import { ProductPage } from "../../../AdminShared/api"
import { errorMessage } from "../../../AdminShared/ui"

export const LAYOUTS = ["phone", "tablet", "web"]
export const LAYOUT_LABELS = { phone: "Phone", tablet: "Tablet", web: "Web" }

// player_state.lua `pageTransitions`
export const TRANSITIONS = ["FadeIn", "Instant"]
// text_panel.lua
export const ALIGNMENTS = ["Left", "Center", "Right"]
export const REVEAL_EFFECTS = ["Instant", "Fade"]
export const TEXT_COLORS = ["black", "white", "red", "yellow"]
// settings_panel.lua
export const BOOK_COLORS = ["black", "white", "#FFB623", "#1E4B93"]
// main.lua `layoutAspectRatioRanges`
export const ASPECT_RANGES = {
  phone: { min: 4 / 3, max: 16 / 9 },
  tablet: { min: 8 / 9, max: 4 / 3 },
  web: { min: 16 / 9, max: 21 / 9 },
}
// background_panel.lua: the two lengths the panel offers outright
export const ANIM_FAST = 5
export const ANIM_SLOW = 9
// text_panel.lua `sizes/1..5`
export const FONT_SIZES = [38, 44, 50, 56, 62]

/** A content key as the string it is, whether it arrived populated or not. */
export const idOf = (value) => (value && typeof value === "object" ? String(value._id) : value ? String(value) : undefined)

export const num = (value) => (value === "" || value === null || value === undefined ? undefined : Number(value))

/**
 * The layer of a page a layout reads. A page may only carry the layout it was
 * authored in, so a missing one falls back the way the player falls back
 * (player_state.lua `fallbackLayouts`).
 */
const FALLBACKS = { phone: ["tablet", "web"], tablet: ["web", "phone"], web: ["tablet", "phone"] }
export const layerOf = (obj, layout) => {
  if (!obj) return null
  for (const name of [layout, ...(FALLBACKS[layout] || [])]) if (obj[name]) return obj[name]
  return null
}
/** The layout whose layer a layout actually reads. */
export const layerLayout = (obj, layout) => {
  if (!obj) return layout
  for (const name of [layout, ...(FALLBACKS[layout] || [])]) if (obj[name]) return name
  return layout
}

/** The keyframe of a background layer: `startTransform` / `endTransform`. */
export const transformOf = (layer, phase) => layer?.[`${phase}Transform`] || { scale: 1, offsetPos: { x: 0, y: 0 } }

/**
 * Operations on the book. `editor` is useProductEditor's object; every write
 * goes through `set` (the document) or `setText` (a key's value), so the
 * editor's own dirty tracking and saving stay in charge.
 */
export function bookOps(editor, { notify } = {}) {
  const { pid, product, set, text, setText, addEntries, language } = editor
  const pages = product?.pages || []
  const say = (message, isError) => notify?.(message, isError)

  const setPages = (next) => set("pages", next)

  /** A fresh scene from the API (it mints the page's content keys). */
  const addPage = async (at = pages.length) => {
    const res = await ProductPage(pid, "new-page")
    if (errorMessage(res) || !res?.page) {
      say(errorMessage(res) || "Could not create the scene", true)
      return null
    }
    addEntries(res.localization)
    const next = [...pages]
    const index = Math.min(Math.max(at, 0), next.length)
    next.splice(index, 0, res.page)
    setPages(next)
    return index
  }

  /**
   * A copy of a scene, next to it: a new page from the API, then the pictures
   * and the keyframes of the original written into its own keys — the same
   * two steps as `clonePage` / `copyPageProperties` in player_state.lua.
   */
  const clonePage = async (index) => {
    const source = pages[index]
    if (!source) return null
    const res = await ProductPage(pid, "new-page")
    if (errorMessage(res) || !res?.page) {
      say(errorMessage(res) || "Could not copy the scene", true)
      return null
    }
    addEntries(res.localization)
    const page = JSON.parse(JSON.stringify(res.page))

    for (const layout of LAYOUTS) {
      const from = source.background?.[layout]
      const to = page.background?.[layout]
      if (!from || !to) continue
      // the picture is a value of the new page's own key
      const value = text(idOf(from.image), language)
      if (value) setText(idOf(to.image), value, language)
      if (from.startTransform) to.startTransform = JSON.parse(JSON.stringify(from.startTransform))
      if (from.endTransform) to.endTransform = JSON.parse(JSON.stringify(from.endTransform))
      if (from.duration !== undefined) to.duration = from.duration
      if (from.verticalFocus !== undefined) to.verticalFocus = from.verticalFocus

      // the look of the words travels with the copy; the words themselves do not
      const fromText = source.subtitle?.[layout]
      const toText = page.subtitle?.[layout]
      if (!fromText || !toText) continue
      for (const field of ["renderBox", "textFrame", "font", "fontSize", "alignment", "color", "revealEffect", "startAt", "duration"]) {
        if (fromText[field] === undefined) continue
        toText[field] = typeof fromText[field] === "object" ? JSON.parse(JSON.stringify(fromText[field])) : fromText[field]
      }
    }
    page.transition = source.transition
    page.sfxLoop = source.sfxLoop
    page.musicLoop = source.musicLoop
    page.usePrevSfx = source.usePrevSfx
    page.usePrevMusic = source.usePrevMusic

    const next = [...pages]
    next.splice(index + 1, 0, page)
    setPages(next)
    return index + 1
  }

  const deletePage = (index) => {
    const next = pages.filter((_, i) => i !== index)
    setPages(next)
    return Math.max(0, Math.min(index, next.length - 1))
  }

  /** Move a scene to another place in the book. */
  const movePage = (from, to) => {
    if (to < 0 || to >= pages.length || from === to) return from
    const next = [...pages]
    const [moved] = next.splice(from, 1)
    next.splice(to, 0, moved)
    setPages(next)
    return to
  }

  const addTrigger = async (index) => {
    const page = pages[index]
    if (!page) return null
    const res = await ProductPage(pid, "new-trigger", idOf(page.title) || idOf(page.subtitle?.web?.text))
    if (errorMessage(res) || !res?.trigger) {
      say(errorMessage(res) || "Could not create the tap area", true)
      return null
    }
    addEntries(res.localization)
    const areas = [...(page.triggerAreas || []), res.trigger]
    set(`pages.${index}.triggerAreas`, areas)
    return areas.length - 1
  }

  const deleteTrigger = (index, order) => {
    const page = pages[index]
    if (!page) return
    set(
      `pages.${index}.triggerAreas`,
      (page.triggerAreas || []).filter((_, i) => i !== order)
    )
  }

  // ---- background keyframes (background_panel.lua) ----------------------

  const copyPhase = (index, layout, from, to) => {
    const layer = pages[index]?.background?.[layout]
    if (!layer?.[`${from}Transform`]) return
    set(`pages.${index}.background.${layout}.${to}Transform`, JSON.parse(JSON.stringify(layer[`${from}Transform`])))
  }

  /**
   * Take a keyframe from another scene. `to: "both"` puts it on both of this
   * scene's keyframes, which is what Alt does in the Lua panel.
   */
  const copyPhaseFromPage = (index, sourceIndex, layout, from, to) => {
    const source = pages[sourceIndex]?.background?.[layout]?.[`${from}Transform`]
    if (!source) return
    const value = JSON.parse(JSON.stringify(source))
    for (const phase of to === "both" ? ["start", "end"] : [to]) {
      set(`pages.${index}.background.${layout}.${phase}Transform`, JSON.parse(JSON.stringify(value)))
    }
  }

  /**
   * Fit the picture to every layout's aspect, the way pressing C does in the
   * Lua editor: the scale that covers the widest and narrowest shape the
   * layout is shown at, and the picture centred.
   */
  const fitToLayouts = (index, imageAspect) => {
    if (!imageAspect) return
    for (const layout of LAYOUTS) {
      const range = ASPECT_RANGES[layout]
      const scale = Math.max(imageAspect / range.min, imageAspect / range.max)
      for (const phase of ["start", "end"]) {
        if (!pages[index]?.background?.[layout]) continue
        set(`pages.${index}.background.${layout}.${phase}Transform`, { scale, offsetPos: { x: 0, y: 0 } })
      }
    }
  }

  // ---- text (text_panel.lua) -------------------------------------------

  const TEXT_STYLE = ["renderBox", "textFrame", "font", "fontSize", "alignment", "color"]

  /** Copy the style of the words from another scene. */
  const copyTextStyle = (index, sourceIndex, layout, fields = TEXT_STYLE) => {
    const source = pages[sourceIndex]?.subtitle?.[layout]
    if (!source) return
    for (const field of fields) {
      if (source[field] === undefined) continue
      set(`pages.${index}.subtitle.${layout}.${field}`, typeof source[field] === "object" ? JSON.parse(JSON.stringify(source[field])) : source[field])
    }
  }

  /** Give every other scene the chosen fields of this one (the template button). */
  const applyToAllPages = (index, layout, fields, kind) => {
    const source = pages[index]
    if (!source) return
    pages.forEach((_, other) => {
      if (other === index) return
      for (const field of fields) {
        if (kind === "text") {
          const value = source.subtitle?.[layout]?.[field]
          if (value === undefined) continue
          set(`pages.${other}.subtitle.${layout}.${field}`, typeof value === "object" ? JSON.parse(JSON.stringify(value)) : value)
          continue
        }
        if (field === "transition") {
          set(`pages.${other}.transition`, source.transition)
          continue
        }
        const value = source.background?.[layout]?.[field]
        if (value === undefined) continue
        set(`pages.${other}.background.${layout}.${field}`, typeof value === "object" ? JSON.parse(JSON.stringify(value)) : value)
      }
    })
  }

  return {
    pages,
    addPage,
    clonePage,
    deletePage,
    movePage,
    addTrigger,
    deleteTrigger,
    copyPhase,
    copyPhaseFromPage,
    fitToLayouts,
    copyTextStyle,
    applyToAllPages,
    TEXT_STYLE,
  }
}
